// Schlankes Kollisionsregister (Konzept §15: kaum Physik).
//
// Achsenorientierte / yaw-gedrehte Boxen + Zylinder. Der Spieler ist ein
// stehender Zylinder. Statische Hindernisse liegen in einem Raumgitter
// (8 m Zellen), damit pro Substep nicht der ganze Hof geprüft wird —
// VR-Budget (§14) und Fixed-Timestep (player.js) verbieten Allokationen
// und O(n)-Schleifen über alles.
//
// Bewegliche Waggons gehören nicht ins Raster: jeder Frame neu, Auflösung
// im Waggon-Lokalraum (dort ist die Hülle achsenorientiert). Decks/Mitfahren
// bleiben in player.js — hier nur Außenhülle, Türöffnung und Anstoßen.

import * as THREE from 'three';
import {
  PLAYER_RADIUS, PLAYER_HEIGHT, MAX_STEP_HEIGHT, GRAB_REACH,
} from '../../shared/constants.js';
import { trackFrame } from '../../shared/track.js';
import { workersAt } from '../../shared/workers.js';

// --- Konstanten -------------------------------------------------------------

/** Zellenkante des statischen Raumgitters (m). */
export const COLLISION_CELL = 8;

/** Max. Kandidaten aus dem Raster pro Abfrage — harte Schranke, kein Wachsen. */
const MAX_STATIC_CANDIDATES = 48;

/** Max. statische Hindernisse gesamt (Höfe + Prellbock + Brückenpfeiler). */
const MAX_STATICS = 512;

/** Max. Waggon-Tests pro Substep (beide Züge). */
const MAX_CARS = 48;

/** Bahnsteige: Oberkante ~railTop+0,4 m über Schiene; Terrain kann tiefer liegen. */
const STEP_SURFACE_MAX = Math.max(MAX_STEP_HEIGHT, 0.95);

/** Weiches Hindernis um Hofarbeiter (Konzept §11: Wege einschränken, nicht fangen). */
const WORKER_RADIUS = 0.48;
const WORKER_PUSH_MAX = 0.12; // m pro Substep — sanft

/** Relativgeschwindigkeit Zug/Spieler, ab der ein Treffer stunnt. */
const HIT_STUN_SPEED = 2.2;

// Flags (Bitmaske, kein String-Vergleich im Hot Path)
export const COL_SOLID = 1;       // blockiert horizontal
export const COL_STEP = 2;        // begehbare Oberkante (Bahnsteig)
export const COL_SOFT = 4;        // nur leicht schieben (Arbeiter)

// --- Modul-Puffer (nichts pro Substep allokieren) ---------------------------

const _frame = {
  pos: { x: 0, y: 0, z: 0 },
  tangent: { x: 0, y: 0, z: 0 },
  right: { x: 0, y: 0, z: 0 },
  heading: 0,
};

const _cand = new Int16Array(MAX_STATIC_CANDIDATES);
const _candSeen = new Uint16Array(MAX_STATICS); // generation stamp
let _candGen = 1;

const _local = { x: 0, y: 0, z: 0 };
const _result = {
  supportY: null,
  hitSolid: false,
  hitMoving: false,
  hitCarId: null,
  tests: 0,
  staticTests: 0,
  carTests: 0,
  pushed: false,
};

/**
 * Ein Eintrag im statischen Register.
 * kind: 0 = OBB (yaw), 1 = Zylinder (Y-Achse).
 * cx,cy,cz = Mittelpunkt; hx,hy,hz = Halbachsen (Zylinder: hx=Radius, hz ungenutzt).
 * c,s = cos/sin(yaw) für OBB.
 */
function makeSlot() {
  return {
    kind: 0,
    cx: 0, cy: 0, cz: 0,
    hx: 0, hy: 0, hz: 0,
    c: 1, s: 0,
    flags: COL_SOLID,
    // AABB fürs Raster
    minX: 0, maxX: 0, minZ: 0, maxZ: 0,
    active: false,
  };
}

export class CollisionSystem {
  async init(ctx) {
    this.statics = new Array(MAX_STATICS);
    for (let i = 0; i < MAX_STATICS; i++) this.statics[i] = makeSlot();
    this.staticCount = 0;

    /** @type {Map<number, number[]>} cellKey → Indizes */
    this.grid = new Map();

    // Arbeiter-Cache: workersAt() allokiert — nur 1×/Frame hier, nie pro Substep.
    this._workers = new Array(16);
    for (let i = 0; i < 16; i++) this._workers[i] = { x: 0, y: 0, z: 0 };
    this._workerCount = 0;

    // Diagnostik (Tests / Debug) — wird pro resolve überschrieben.
    this.lastStats = {
      tests: 0,
      staticTests: 0,
      carTests: 0,
      staticCount: 0,
      cellsTouched: 0,
    };

    // ctx-Vertrag: andere Systeme registrieren über ctx.collision
    ctx.collision = this;
    void ctx;
  }

  update(_dt, ctx) {
    // Statik ändert sich nicht; Waggons werden pro resolve aus ctx gelesen.
    // Arbeiter-Positionen einmal pro Frame puffern (kein Array pro Substep).
    this._workerCount = 0;
    if (ctx?.worldSeconds == null) return;
    const list = workersAt(ctx.worldSeconds);
    const n = Math.min(list.length, this._workers.length);
    for (let i = 0; i < n; i++) {
      const w = list[i];
      const slot = this._workers[i];
      slot.x = w.x; slot.y = w.y; slot.z = w.z;
    }
    this._workerCount = n;
  }

  // --- Registrierung (nur in init der Welt-Systeme) -------------------------

  /** Alle statischen Hindernisse verwerfen und Raster leeren. */
  clearStatic() {
    for (let i = 0; i < this.staticCount; i++) this.statics[i].active = false;
    this.staticCount = 0;
    this.grid.clear();
  }

  /**
   * Yaw-gedrehte Box (Mittelpunkt + Halbachsen).
   * Lokal: X = Breite, Y = Höhe, Z = Tiefe — wie three.js-Box nach rotation.y.
   * @param {number} flags COL_SOLID | COL_STEP | …
   * @returns {number} Index oder -1
   */
  addBox(cx, cy, cz, hx, hy, hz, yaw = 0, flags = COL_SOLID) {
    if (this.staticCount >= MAX_STATICS) return -1;
    const i = this.staticCount++;
    const o = this.statics[i];
    o.kind = 0;
    o.cx = cx; o.cy = cy; o.cz = cz;
    o.hx = hx; o.hy = hy; o.hz = hz;
    o.c = Math.cos(yaw);
    o.s = Math.sin(yaw);
    o.flags = flags;
    o.active = true;
    // AABB der OBB in XZ
    const extX = hx * Math.abs(o.c) + hz * Math.abs(o.s);
    const extZ = hx * Math.abs(o.s) + hz * Math.abs(o.c);
    o.minX = cx - extX; o.maxX = cx + extX;
    o.minZ = cz - extZ; o.maxZ = cz + extZ;
    this._insertGrid(i, o);
    return i;
  }

  /**
   * Stehender Zylinder (Masten, dünne Pfosten).
   * cy = Mitte, hy = Halbhöhe, radius = hx.
   */
  addCylinder(cx, cy, cz, radius, hy, flags = COL_SOLID) {
    if (this.staticCount >= MAX_STATICS) return -1;
    const i = this.staticCount++;
    const o = this.statics[i];
    o.kind = 1;
    o.cx = cx; o.cy = cy; o.cz = cz;
    o.hx = radius; o.hy = hy; o.hz = 0;
    o.c = 1; o.s = 0;
    o.flags = flags;
    o.active = true;
    o.minX = cx - radius; o.maxX = cx + radius;
    o.minZ = cz - radius; o.maxZ = cz + radius;
    this._insertGrid(i, o);
    return i;
  }

  /**
   * Box entlang der Strecke (s, lateral) — gleiches Koordinatensystem wie station.js.
   * halfLat = Halbbreite quer zum Gleis, halfAlong = Halblänge längs s, halfH = Halbhöhe.
   * cy optional: absolute Y-Mitte; sonst trackY + halfH.
   */
  addTrackBox(s, lateral, halfLat, halfH, halfAlong, opts = {}) {
    const branch = opts.branch ?? 'main';
    trackFrame(s, _frame, branch);
    const cx = _frame.pos.x + _frame.right.x * lateral;
    const cz = _frame.pos.z + _frame.right.z * lateral;
    const cy = opts.cy != null ? opts.cy : (_frame.pos.y + halfH);
    return this.addBox(cx, cy, cz, halfLat, halfH, halfAlong, _frame.heading, opts.flags ?? COL_SOLID);
  }

  _insertGrid(index, o) {
    const c0 = Math.floor(o.minX / COLLISION_CELL);
    const c1 = Math.floor(o.maxX / COLLISION_CELL);
    const r0 = Math.floor(o.minZ / COLLISION_CELL);
    const r1 = Math.floor(o.maxZ / COLLISION_CELL);
    for (let ix = c0; ix <= c1; ix++) {
      for (let iz = r0; iz <= r1; iz++) {
        const key = cellKey(ix, iz);
        let list = this.grid.get(key);
        if (!list) {
          list = [];
          this.grid.set(key, list);
        }
        list.push(index);
      }
    }
  }

  /**
   * Kandidaten um (x,z) in vorallokiertes _cand schreiben.
   * @returns {number} Anzahl
   */
  _queryStatic(x, z, radius) {
    // Generation-Stamp statt Set — kein Allok.
    _candGen++;
    if (_candGen > 65000) {
      _candSeen.fill(0);
      _candGen = 1;
    }
    let n = 0;
    let cells = 0;
    const pad = radius + 0.5;
    const c0 = Math.floor((x - pad) / COLLISION_CELL);
    const c1 = Math.floor((x + pad) / COLLISION_CELL);
    const r0 = Math.floor((z - pad) / COLLISION_CELL);
    const r1 = Math.floor((z + pad) / COLLISION_CELL);
    for (let ix = c0; ix <= c1; ix++) {
      for (let iz = r0; iz <= r1; iz++) {
        const list = this.grid.get(cellKey(ix, iz));
        if (!list) continue;
        cells++;
        for (let k = 0; k < list.length; k++) {
          const idx = list[k];
          if (idx < 0 || idx >= this.staticCount) continue;
          if (_candSeen[idx] === _candGen) continue;
          _candSeen[idx] = _candGen;
          if (n < MAX_STATIC_CANDIDATES) _cand[n++] = idx;
        }
      }
    }
    this._lastCells = cells;
    return n;
  }

  // --- Auflösung ------------------------------------------------------------

  /**
   * Schiebt den Spieler-Zylinder aus Hindernissen und liefert Support-Höhe.
   * Mutiert pos (x/y/z) und optional velocity (horizontale Komponente dämpfen).
   *
   * @param {{x:number,y:number,z:number}} pos Füße
   * @param {{x:number,y:number,z:number}|null} velocity
   * @param {object} opts
   * @returns {typeof _result}
   */
  resolvePlayer(pos, velocity, opts = {}) {
    const radius = opts.radius ?? PLAYER_RADIUS;
    const bodyH = opts.bodyHeight ?? PLAYER_HEIGHT;
    const maxStep = opts.maxStep ?? MAX_STEP_HEIGHT;
    const trains = opts.trains ?? null;
    const includeWorkers = opts.workers !== false;

    let tests = 0;
    let staticTests = 0;
    let carTests = 0;
    let supportY = null;
    let hitSolid = false;
    let hitMoving = false;
    let hitCarId = null;
    let pushed = false;

    // Mehrere Iterationen: überlappende Boxen (Ecke Mauer/Bahnsteig).
    for (let iter = 0; iter < 3; iter++) {
      const n = this._queryStatic(pos.x, pos.z, radius + 1.0);
      let moved = false;

      for (let i = 0; i < n; i++) {
        const o = this.statics[_cand[i]];
        if (!o || !o.active) continue;
        staticTests++;
        tests++;
        const r = this._resolveOneStatic(pos, radius, bodyH, maxStep, o);
        if (r.supportY != null) {
          if (supportY == null || r.supportY > supportY) supportY = r.supportY;
        }
        if (r.pushed) {
          pushed = true;
          moved = true;
          if (!(o.flags & COL_SOFT)) hitSolid = true;
        }
      }

      // Waggons (außen) — nur ground/air, nie im ride-Bezugssystem.
      if (trains && opts.cars !== false) {
        for (let t = 0; t < trains.length; t++) {
          const train = trains[t];
          if (!train?.cars) continue;
          const cars = train.cars;
          const limit = Math.min(cars.length, MAX_CARS);
          for (let ci = 0; ci < limit; ci++) {
            const car = cars[ci];
            if (!car?.type) continue;
            carTests++;
            tests++;
            const cr = this._resolveCar(pos, radius, bodyH, car, train);
            if (cr.pushed) {
              pushed = true;
              moved = true;
              hitSolid = true;
              if (cr.moving) {
                hitMoving = true;
                hitCarId = car.id;
              }
            }
            if (cr.supportY != null) {
              if (supportY == null || cr.supportY > supportY) supportY = cr.supportY;
            }
          }
        }
      }

      if (!moved) break;
    }

    // Arbeiter: weich, aus Frame-Cache (update) — Konzept §11.
    if (includeWorkers) {
      for (let i = 0; i < this._workerCount; i++) {
        const w = this._workers[i];
        tests++;
        const dx = pos.x - w.x;
        const dz = pos.z - w.z;
        const dist = Math.hypot(dx, dz);
        const minD = radius + WORKER_RADIUS;
        if (dist < minD && dist > 1e-6) {
          if (Math.abs(pos.y - w.y) < 1.6) {
            const push = Math.min(WORKER_PUSH_MAX, minD - dist);
            pos.x += (dx / dist) * push;
            pos.z += (dz / dist) * push;
            pushed = true;
          }
        } else if (dist <= 1e-6) {
          pos.x += WORKER_PUSH_MAX;
          pushed = true;
        }
      }
    }

    // Bei hartem Stoß horizontale Geschwindigkeit dämpfen (kein Durchrutschen).
    if (pushed && hitSolid && velocity) {
      velocity.x *= 0.35;
      velocity.z *= 0.35;
    }

    // Fahrender Zug: spürbarer Impuls + Stun-Flag (player wendet stunT an).
    if (hitMoving && velocity) {
      // leichte seitliche/rückwärtige Komponente steckt schon in pos-Push;
      // vy anheben, damit es nicht wie eine Wand ohne Folgen wirkt.
      if (velocity.y < 1.2) velocity.y = 1.2;
    }

    _result.supportY = supportY;
    _result.hitSolid = hitSolid;
    _result.hitMoving = hitMoving;
    _result.hitCarId = hitCarId;
    _result.tests = tests;
    _result.staticTests = staticTests;
    _result.carTests = carTests;
    _result.pushed = pushed;

    this.lastStats.tests = tests;
    this.lastStats.staticTests = staticTests;
    this.lastStats.carTests = carTests;
    this.lastStats.staticCount = this.staticCount;
    this.lastStats.cellsTouched = this._lastCells ?? 0;

    return _result;
  }

  /**
   * Ein statisches Hindernis gegen Spieler-Zylinder.
   * Schreibt pos bei Push; liefert supportY wenn begehbar.
   */
  _resolveOneStatic(pos, radius, bodyH, maxStep, o) {
    const out = { pushed: false, supportY: null };
    const boxMinY = o.cy - o.hy;
    const boxMaxY = o.cy + o.hy;
    const pMinY = pos.y;
    const pMaxY = pos.y + bodyH;

    // Vertikal getrennte Körper — kein Kontakt.
    if (pMaxY < boxMinY + 0.02 || pMinY > boxMaxY - 0.02) return out;

    if (o.kind === 1) {
      // Zylinder
      const dx = pos.x - o.cx;
      const dz = pos.z - o.cz;
      const dist = Math.hypot(dx, dz);
      const minD = radius + o.hx;
      if (dist >= minD) return out;

      // Step-up auf Zylinder-Deckel (selten; Lampen nicht steppable)
      if ((o.flags & COL_STEP) && pos.y >= boxMaxY - STEP_SURFACE_MAX && pos.y <= boxMaxY + 0.1) {
        out.supportY = boxMaxY;
        return out;
      }
      if (!(o.flags & COL_SOLID) && !(o.flags & COL_SOFT)) return out;

      const pen = minD - (dist < 1e-8 ? 0 : dist);
      if (dist < 1e-8) {
        pos.x += minD;
      } else {
        const s = pen / dist;
        pos.x += dx * s;
        pos.z += dz * s;
      }
      out.pushed = true;
      return out;
    }

    // OBB → Lokal
    const dx = pos.x - o.cx;
    const dz = pos.z - o.cz;
    // three.js Y-Rot: world = R * local → local = R^T * world
    const lx = dx * o.c - dz * o.s;
    const lz = dx * o.s + dz * o.c;

    const hx = o.hx + radius;
    const hz = o.hz + radius;
    if (Math.abs(lx) > hx || Math.abs(lz) > hz) return out;

    // Begehbare Oberkante: lieber hochsteigen als seitlich blocken.
    // Sonst bleibt man ewig vor dem Bahnsteig (STEP > MAX_STEP_HEIGHT).
    if (o.flags & COL_STEP) {
      const rise = boxMaxY - pos.y;
      if (rise >= -0.12 && rise <= STEP_SURFACE_MAX) {
        out.supportY = boxMaxY;
        // Kein horizontaler Push — Ground-Resolve hebt die Füße auf die Kante.
        return out;
      }
      // Schon oben: Support halten, nicht von der Kante schieben.
      if (pos.y >= boxMaxY - 0.05 && pos.y <= boxMaxY + 0.35) {
        out.supportY = boxMaxY;
        return out;
      }
    }
    void maxStep;

    if (!(o.flags & COL_SOLID) && !(o.flags & COL_SOFT)) return out;

    // Kürzester Ausweg in XZ (Lokal)
    const penX = hx - Math.abs(lx);
    const penZ = hz - Math.abs(lz);
    let nlx = lx;
    let nlz = lz;
    if (penX < penZ) {
      nlx = lx >= 0 ? hx : -hx;
    } else {
      nlz = lz >= 0 ? hz : -hz;
    }
    // zurück nach Welt
    const wx = nlx * o.c + nlz * o.s;
    const wz = -nlx * o.s + nlz * o.c;
    pos.x = o.cx + wx;
    pos.z = o.cz + wz;
    out.pushed = true;
    return out;
  }

  /**
   * Außenhülle eines Waggons im Waggon-Lokalraum.
   * Offene Boxcar-Tür: Durchgang in der Türscheibe; geschlossen: volle Box.
   */
  _resolveCar(pos, radius, bodyH, car, train) {
    const out = { pushed: false, supportY: null, moving: false };
    const type = car.type;
    if (!type) return out;

    // Welt → Waggon-Lokal (existierende API, kein group.matrix nötig)
    if (!train.worldToLocal) return out;
    _local.x = 0; _local.y = 0; _local.z = 0;
    // worldToLocal erwartet Vector3-artig — plain object reicht, wenn es .x/y/z liest.
    const wref = _carWorld;
    wref.x = pos.x; wref.y = pos.y; wref.z = pos.z;
    train.worldToLocal(car.id, wref, _carLocal);

    const halfW = type.width * 0.5;
    const halfL = type.length * 0.5;
    // Unterkante knapp über Schiene, Oberkante = height (Typ-Maße ab Bodenplatte).
    const y0 = 0.15;
    const y1 = type.height;
    const lx = _carLocal.x;
    const ly = _carLocal.y;
    const lz = _carLocal.z;

    const pMaxY = ly + bodyH;
    if (pMaxY < y0 || ly > y1) return out;

    // Expandiert um Radius
    const hx = halfW + radius;
    const hz = halfL + radius;
    if (Math.abs(lx) > hx || Math.abs(lz) > hz) return out;

    // Türpassage: offene Boxcar-Tür — durch die Seitenöffnung laufen (Konzept: ehrlich).
    if (car.doorOpen && type.interior?.doors?.length) {
      for (let d = 0; d < type.interior.doors.length; d++) {
        const door = type.interior.doors[d];
        const doorHalf = (door.width ?? 2.4) * 0.5 + radius;
        const z0 = (door.offset ?? 0) - doorHalf;
        const z1 = (door.offset ?? 0) + doorHalf;
        if (lz >= z0 && lz <= z1) {
          // Türhöhe: Boden bis ~2,4 m über Deck
          const floorY = type.interior.floorY ?? type.deckHeight;
          if (ly < floorY + 2.5 && pMaxY > floorY - 0.2) {
            // In der Türscheibe: keine Außenkollision (Durchgang).
            return out;
          }
        }
      }
    }

    // Flachwagen / niedrige Decks: Oberseite begehbar nur wenn man schon oben ist
    // (Aufspringen bleibt Grab — von der Seite blockt die Hülle).
    const deckY = type.deckHeight ?? y1 * 0.5;
    if (type.ride === 'top' && ly >= deckY - 0.05 && ly <= deckY + 0.35) {
      // Auf dem Deck: Support, aber nur wenn innerhalb der Deck-XZ (ohne großen Radius)
      if (Math.abs(lx) <= halfW + 0.05 && Math.abs(lz) <= halfL + 0.05) {
        // world Y für Support: deck in Welt
        if (train.localToWorld) {
          _carLocal.x = lx; _carLocal.y = deckY; _carLocal.z = lz;
          train.localToWorld(car.id, _carLocal, _carWorld);
          out.supportY = _carWorld.y;
        }
        return out;
      }
    }

    // Kürzester horizontaler Ausweg im Lokalraum
    const penX = hx - Math.abs(lx);
    const penZ = hz - Math.abs(lz);
    let nlx = lx;
    let nlz = lz;
    if (penX < penZ) {
      nlx = lx >= 0 ? hx : -hx;
    } else {
      nlz = lz >= 0 ? hz : -hz;
    }

    // Lokal → Welt (nur XZ; Y bleibt Spieler-Y)
    _carLocal.x = nlx;
    _carLocal.y = ly;
    _carLocal.z = nlz;
    if (train.localToWorld) {
      train.localToWorld(car.id, _carLocal, _carWorld);
      pos.x = _carWorld.x;
      pos.z = _carWorld.z;
    }
    out.pushed = true;

    const real = Math.abs(train.realSpeed ?? 0);
    if (real >= HIT_STUN_SPEED) out.moving = true;

    return out;
  }

  /**
   * Nur Support-Höhe abfragen (ohne zu schieben) — für Tests / optionale Nutzung.
   */
  querySupportY(x, y, z, radius = PLAYER_RADIUS) {
    let supportY = null;
    const n = this._queryStatic(x, z, radius + 0.5);
    for (let i = 0; i < n; i++) {
      const o = this.statics[_cand[i]];
      if (!o?.active || !(o.flags & COL_STEP)) continue;
      const boxMaxY = o.cy + o.hy;
      if (y < boxMaxY - STEP_SURFACE_MAX || y > boxMaxY + 0.15) continue;
      // XZ-Test grob über expandierte AABB
      if (x < o.minX - radius || x > o.maxX + radius) continue;
      if (z < o.minZ - radius || z > o.maxZ + radius) continue;
      if (supportY == null || boxMaxY > supportY) supportY = boxMaxY;
    }
    return supportY;
  }

  /**
   * Kantensuche für Mantling (additiv, kein Push).
   * Sucht vor (x,z) in Blickrichtung eine Oberkante in [minRise, maxRise]
   * über den Füßen, mit freiem Raum darüber für den Körper.
   *
   * Kandidatenzahl bleibt durchs Raster begrenzt (MAX_STATIC_CANDIDATES).
   * @returns {{ topY, standX, standZ } | null}
   */
  findMantleEdge(x, y, z, fx, fz, opts = {}) {
    const minRise = opts.minRise ?? 0.5;
    const maxRise = opts.maxRise ?? 1.9;
    const forward = opts.forward ?? 0.55;
    const radius = opts.radius ?? PLAYER_RADIUS;
    const bodyH = opts.bodyHeight ?? PLAYER_HEIGHT;

    const px = x + fx * forward;
    const pz = z + fz * forward;
    const n = this._queryStatic(px, pz, radius + forward + 0.4);

    let best = null;
    let bestScore = Infinity;

    for (let i = 0; i < n; i++) {
      const o = this.statics[_cand[i]];
      if (!o?.active) continue;
      // Weiche Hindernisse (Arbeiter) sind keine Kanten.
      if (o.flags & COL_SOFT) continue;
      if (!(o.flags & (COL_SOLID | COL_STEP))) continue;

      const topY = o.cy + o.hy;
      const rise = topY - y;
      if (rise < minRise || rise > maxRise) continue;

      // Probe muss die Box in XZ treffen (expandiert um radius).
      if (px < o.minX - radius || px > o.maxX + radius) continue;
      if (pz < o.minZ - radius || pz > o.maxZ + radius) continue;

      // Platz über der Kante: Kopf darf nicht in eine höhere Box stecken.
      // Grobe Prüfung: Support etwas über topY frei (kein zweites solid darüber).
      let blocked = false;
      const headY = topY + bodyH * 0.85;
      for (let j = 0; j < n; j++) {
        const b = this.statics[_cand[j]];
        if (!b?.active || b === o) continue;
        if (!(b.flags & COL_SOLID) || (b.flags & COL_SOFT)) continue;
        const bTop = b.cy + b.hy;
        const bBot = b.cy - b.hy;
        if (headY < bBot || topY + 0.1 > bTop) continue;
        if (px < b.minX - radius || px > b.maxX + radius) continue;
        if (pz < b.minZ - radius || pz > b.maxZ + radius) continue;
        blocked = true;
        break;
      }
      if (blocked) continue;

      // Standpunkt: etwas auf die Fläche gezogen (Mittelpunkt der Box-XZ
      // gewichtet mit Vorwärtspunkt).
      const standX = THREE.MathUtils.clamp(px, o.minX + 0.05, o.maxX - 0.05);
      const standZ = THREE.MathUtils.clamp(pz, o.minZ + 0.05, o.maxZ - 0.05);
      // Bevorzuge Kanten knapp über Hüfte und nah.
      const score = Math.abs(rise - 1.1) + Math.hypot(standX - x, standZ - z) * 0.5;
      if (score < bestScore) {
        bestScore = score;
        best = { topY, standX, standZ };
      }
    }
    return best;
  }

  dispose() {
    this.clearStatic();
  }
}

// Scratch für Waggon-Transform. train.worldToLocal/localToWorld rufen
// outVec3.copy() auf — plain {x,y,z} wirft (TypeError). Vector3 nötig.
const _carWorld = new THREE.Vector3();
const _carLocal = new THREE.Vector3();

function cellKey(ix, iz) {
  // 32-bit-Mix; negativ ok als Map-Key (number).
  return (ix * 73856093) ^ (iz * 19349663);
}

/** Für Tests: Distanz Spieler-Mittelpunkt → greifbarer Waggonrand darf GRAB_REACH nicht sprengen. */
export function grabClearanceOk(playerRadius = PLAYER_RADIUS) {
  // Nach Push: Abstand Wand→Griff ≈ 0, Spieler-Mitte → Wand = radius ≪ GRAB_REACH.
  return playerRadius + 0.05 < GRAB_REACH;
}
